import classNames from 'classnames'
import { motion, type HTMLMotionProps } from 'framer-motion'
import React from 'react'

import { TestingProps } from '@ui/common/component.props'
import { TextColorType } from '@ui/design-system/colors/colors'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'
import { IconFCType } from '@ui/icons/types'

import styles from './Chip.module.scss'

interface ChipProps
  extends Omit<HTMLMotionProps<'button'>, 'children' | 'onClick' | 'color'>,
    TestingProps {
  selectedVariant?: ChipVariant
  size?: ChipSize
  disabled?: boolean
  selected?: boolean
  draggable?: boolean
  iconLeft?: IconFCType
  iconRight?: IconFCType
  children?: string | number
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onDelete?: (event: React.MouseEvent<HTMLSpanElement>) => void
  iconColor?: TextColorType
}

type ChipSize = 'xs' | 'sm' | 'md'
type ChipVariant = 'default' | 'neutral'

const variantSelectedStyles: Record<ChipVariant, string> = {
  default: styles.variantSelectedDefault,
  neutral: styles.variantSelectedNeutral,
} as const

export const Chip: React.FC<ChipProps> = (props) => {
  const {
    selectedVariant = 'default',
    size = 'md',
    selected = false,
    disabled = false,
    'aria-disabled': ariaDisabledProp = false,
    draggable = false,
    iconLeft: IconLeft,
    iconRight: IconRight,
    onClick,
    onDelete,
    children,
    ...restProps
  } = props

  const isAriaDisabled = ariaDisabledProp === true || ariaDisabledProp === 'true'
  const isDisabledState = disabled || isAriaDisabled

  const chipClasses = classNames(
    styles.base,
    styles[`size-${size}`],
    variantSelectedStyles[selectedVariant],
    {
      [styles.stateSelected]: selected,
      [styles.stateDisabled]: isDisabledState,
      [styles.stateDraggable]: draggable,
    }
  )

  const iconSize = getIconSize(size)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabledState && onClick) {
      onClick(event)
    }
  }

  const handleDelete = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!isDisabledState && onDelete) {
      onDelete(event)
    }
  }

  const motionProps: HTMLMotionProps<'button'> = !isDisabledState
    ? {
        whileHover: { scale: 1.05 },
        whileTap: { scale: 0.95 },
      }
    : {}

  return (
    <motion.button
      className={chipClasses}
      disabled={disabled}
      aria-disabled={isDisabledState}
      draggable={draggable}
      onClick={handleClick}
      tabIndex={isDisabledState ? -1 : 0}
      type='button'
      {...motionProps}
      {...restProps}
    >
      {IconLeft && (
        <span className={styles.iconLeft}>
          <IconLeft color='currentColor' size={iconSize} />
        </span>
      )}
      {children && <span className={styles.text}>{children}</span>}
      {IconRight && !onDelete && (
        <span className={styles.iconRight}>
          <IconRight color='currentColor' size={iconSize} />
        </span>
      )}
      {onDelete && (
        <span className={styles.iconRight} onClick={handleDelete}>
          <CircleCloseIcon color='currentColor' size={iconSize} />
        </span>
      )}
    </motion.button>
  )
}

const getIconSize = (size: ChipSize): number => {
  return {
    xs: 14,
    sm: 16,
    md: 18,
  }[size]
}
